import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Mail, Phone, Calendar, MessageSquare, Home, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [contacts, setContacts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contactsRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/contacts`),
        axios.get(`${API}/property-inquiries`)
      ]);
      setContacts(contactsRes.data);
      setInquiries(inquiriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      new: 'bg-green-100 text-green-800',
      contacted: 'bg-blue-100 text-blue-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      new: 'Nuevo',
      contacted: 'Contactado',
      closed: 'Cerrado'
    };
    return (
      <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-golden-600 to-golden-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold">Panel de Administración</h1>
          <p className="text-golden-100 mt-2">GUDO Real Estate - Gestión de Contactos</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Contactos</p>
                  <p className="text-3xl font-bold text-gray-900">{contacts.length}</p>
                </div>
                <Mail className="w-12 h-12 text-golden-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Solicitudes de Propiedades</p>
                  <p className="text-3xl font-bold text-gray-900">{inquiries.length}</p>
                </div>
                <Home className="w-12 h-12 text-golden-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Nuevos (sin contactar)</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {[...contacts, ...inquiries].filter(i => i.status === 'new').length}
                  </p>
                </div>
                <MessageSquare className="w-12 h-12 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Refresh Button */}
        <div className="flex justify-end mb-4">
          <Button onClick={fetchData} disabled={loading} className="bg-golden-600 hover:bg-golden-700">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="contacts" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="contacts">
              Contactos Generales ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="inquiries">
              Solicitudes de Propiedades ({inquiries.length})
            </TabsTrigger>
          </TabsList>

          {/* Contactos Generales */}
          <TabsContent value="contacts" className="space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Cargando contactos...</p>
              </div>
            ) : contacts.length > 0 ? (
              contacts.map((contact) => (
                <Card key={contact.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl">{contact.name}</CardTitle>
                        <p className="text-sm text-gray-600 mt-1">{contact.subject}</p>
                      </div>
                      {getStatusBadge(contact.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center text-gray-700">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        <a href={`mailto:${contact.email}`} className="hover:text-golden-600">
                          {contact.email}
                        </a>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                        <a href={`tel:${contact.phone}`} className="hover:text-golden-600">
                          {contact.phone}
                        </a>
                      </div>
                      {contact.appointment_date && (
                        <div className="flex items-center text-gray-700">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          <span>Cita preferida: {contact.appointment_date}</span>
                        </div>
                      )}
                      <div className="bg-gray-50 p-4 rounded-lg mt-4">
                        <p className="text-sm font-semibold text-gray-700 mb-1">Mensaje:</p>
                        <p className="text-gray-600">{contact.message}</p>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        Recibido: {formatDate(contact.created_at)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No hay contactos aún</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Solicitudes de Propiedades */}
          <TabsContent value="inquiries" className="space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Cargando solicitudes...</p>
              </div>
            ) : inquiries.length > 0 ? (
              inquiries.map((inquiry) => (
                <Card key={inquiry.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl">{inquiry.name}</CardTitle>
                        <p className="text-sm text-golden-600 font-semibold mt-1">
                          {inquiry.property_title}
                        </p>
                      </div>
                      {getStatusBadge(inquiry.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center text-gray-700">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        <a href={`mailto:${inquiry.email}`} className="hover:text-golden-600">
                          {inquiry.email}
                        </a>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                        <a href={`tel:${inquiry.phone}`} className="hover:text-golden-600">
                          {inquiry.phone}
                        </a>
                      </div>
                      {inquiry.appointment_date && (
                        <div className="flex items-center text-gray-700">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          <span>Cita preferida: {inquiry.appointment_date}</span>
                        </div>
                      )}
                      <div className="bg-gray-50 p-4 rounded-lg mt-4">
                        <p className="text-sm font-semibold text-gray-700 mb-1">Mensaje:</p>
                        <p className="text-gray-600">{inquiry.message}</p>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        Recibido: {formatDate(inquiry.created_at)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No hay solicitudes de propiedades aún</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
